import React, { useState, useEffect } from "react";
import {
  ShieldCheck,
  Monitor,
  Wrench,
  Calendar,
  ShoppingBag,
  AlertCircle,
  Check,
  Clock,
  Info,
  Loader2,
  Pencil,
  X,
  Trash2,
  User,
} from "lucide-react";
import { Sheet, SheetContent } from "../../components/ui/Sheet";
import { Button } from "../../components/ui/button";
import { cn } from "../../lib/utils";
import {
  updateMemberAccess,
  removeFamilyMember,
  FamilyMember,
  HomeRole,
  ServiceType,
  ALL_SERVICE_TYPES,
  SERVICE_TYPE_LABELS,
} from "../../services/family.api";
import { getHomeAssets, Asset } from "../../services/asset.api";

const SERVICE_ICONS: Record<ServiceType, React.ReactNode> = {
  maintenance: <Wrench className="h-4 w-4" />,
  warranty: <ShieldCheck className="h-4 w-4" />,
  bookings: <Calendar className="h-4 w-4" />,
  issues: <AlertCircle className="h-4 w-4" />,
  orders: <ShoppingBag className="h-4 w-4" />,
  protection_plans: <ShieldCheck className="h-4 w-4" />,
};

const ROLE_OPTIONS: { value: HomeRole; label: string; desc: string }[] = [
  { value: "owner", label: "Co-Owner", desc: "Full access to everything" },
  { value: "member", label: "Member", desc: "Access to selected assets & services" },
  { value: "viewer", label: "Viewer", desc: "View-only access" },
];

const RELATIONS = [
  "Spouse", "Partner", "Child", "Parent", "Sibling",
  "Caretaker", "Tenant", "Other",
];

interface EditMemberDrawerProps {
  member: FamilyMember;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

export function EditMemberDrawer({ member, open, onOpenChange, onSuccess }: EditMemberDrawerProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [role, setRole] = useState<HomeRole>(member.role);
  const [relation, setRelation] = useState(member.relation ?? "");
  const [grantFutureAssets, setGrantFutureAssets] = useState(member.grantFutureAssets);
  const [selectedAssetIds, setSelectedAssetIds] = useState<Set<string>>(
    new Set(member.assetAccess.map((a) => a.assetId))
  );
  const [selectedServices, setSelectedServices] = useState<Set<ServiceType>>(
    new Set(member.serviceAccess.map((s) => s.serviceType))
  );

  const [assets, setAssets] = useState<Asset[]>([]);
  const [assetsLoading, setAssetsLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [error, setError] = useState("");

  const isCoOwner = role === "owner";

  useEffect(() => {
    if (open) {
      setIsEditing(false);
      setRole(member.role);
      setRelation(member.relation ?? "");
      setGrantFutureAssets(member.grantFutureAssets);
      setSelectedAssetIds(new Set(member.assetAccess.map((a) => a.assetId)));
      setSelectedServices(new Set(member.serviceAccess.map((s) => s.serviceType)));
      setError("");
      loadAssets();
    }
  }, [open]);

  const loadAssets = async () => {
    setAssetsLoading(true);
    try {
      const data = await getHomeAssets(member.homeId);
      setAssets(data);
    } catch { /* silent */ }
    finally { setAssetsLoading(false); }
  };

  const toggleAsset = (id: string) => {
    setSelectedAssetIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAllAssets = () => {
    setSelectedAssetIds((prev) => {
      if (prev.size === assets.length) return new Set();
      return new Set(assets.map((a) => a.id));
    });
  };

  const toggleService = (s: ServiceType) => {
    setSelectedServices((prev) => {
      const next = new Set(prev);
      if (next.has(s)) next.delete(s);
      else next.add(s);
      return next;
    });
  };

  const selectAllServices = () => {
    setSelectedServices((prev) => {
      if (prev.size === ALL_SERVICE_TYPES.length) return new Set();
      return new Set(ALL_SERVICE_TYPES);
    });
  };

  const handleSave = async () => {
    setError("");
    setSaving(true);
    try {
      await updateMemberAccess(member.id, {
        role,
        relation: relation || undefined,
        grantFutureAssets: isCoOwner ? true : grantFutureAssets,
        assetIds: isCoOwner ? [] : Array.from(selectedAssetIds),
        serviceTypes: isCoOwner ? [] : Array.from(selectedServices),
      });
      setIsEditing(false);
      onSuccess();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Failed to update access");
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async () => {
    if (!window.confirm("Remove " + member.user.name + "? This action cannot be undone.")) return;
    setRemoving(true);
    try {
      await removeFamilyMember(member.id);
      onOpenChange(false);
      onSuccess();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Failed to remove member");
    } finally {
      setRemoving(false);
    }
  };

  const toggleEdit = () => {
    if (isEditing) {
      setRole(member.role);
      setRelation(member.relation ?? "");
      setGrantFutureAssets(member.grantFutureAssets);
      setSelectedAssetIds(new Set(member.assetAccess.map((a) => a.assetId)));
      setSelectedServices(new Set(member.serviceAccess.map((s) => s.serviceType)));
    }
    setIsEditing((v) => !v);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange} side="right">
      <SheetContent side="right" className="w-full sm:max-w-lg [&>button]:hidden p-0">
        <div className="sticky top-0 bg-background z-10 border-b px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-foreground/5 rounded-lg">
                <ShieldCheck className="h-5 w-5 text-foreground" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-foreground leading-none">
                  {isEditing ? "Edit Access" : "Member Access"}
                </h2>
                <p className="text-xs text-muted-foreground mt-1">{member.user.name} · {member.user.email}</p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <Button variant="ghost" size="icon" onClick={toggleEdit} className="h-8 w-8">
                {isEditing ? <X className="h-4 w-4" /> : <Pencil className="h-4 w-4" />}
              </Button>
              <Button variant="ghost" size="icon" onClick={() => onOpenChange(false)} className="h-8 w-8">
                <X className="h-5 w-5" />
              </Button>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          {error && <div className="p-3 rounded-lg bg-destructive/10 text-destructive text-sm">{error}</div>}

          {/* Profile card */}
          <div className="flex items-center gap-4 p-4 rounded-xl bg-secondary/20">
            <div className="h-14 w-14 rounded-full bg-secondary flex items-center justify-center shrink-0">
              <User className="h-6 w-6 text-muted-foreground" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <p className="text-sm font-semibold">{member.user.name}</p>
                <span className={cn(
                  "px-2 py-0.5 rounded-full text-[10px] font-semibold capitalize",
                  member.role === "owner" ? "bg-foreground text-background" :
                  member.role === "viewer" ? "bg-muted-foreground/20 text-muted-foreground" :
                  "bg-green-100 text-green-700"
                )}>
                  {member.role}
                </span>
              </div>
              <p className="text-xs text-muted-foreground">{member.user.email}</p>
              {member.relation && (
                <p className="text-xs text-muted-foreground mt-0.5">{member.relation}</p>
              )}
            </div>
          </div>

          {/* Role picker — edit mode only */}
          {isEditing && (
            <div className="space-y-2">
              <label className="text-sm font-medium">Role</label>
              <div className="grid grid-cols-3 gap-2">
                {ROLE_OPTIONS.map((r) => (
                  <button key={r.value} type="button" onClick={() => setRole(r.value)}
                    className={cn("p-2.5 rounded-lg border text-center transition-colors",
                      role === r.value ? "border-foreground bg-secondary" : "hover:bg-secondary/50")}>
                    <p className="text-xs font-semibold">{r.label}</p>
                    <p className="text-[10px] text-muted-foreground mt-0.5">{r.desc}</p>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Relation — edit mode only */}
          {isEditing && (
            <div className="space-y-1.5">
              <label className="text-sm font-medium">
                Relation <span className="text-muted-foreground font-normal">(optional)</span>
              </label>
              <div className="flex flex-wrap gap-1.5">
                {RELATIONS.map((rel) => (
                  <button key={rel} type="button"
                    onClick={() => setRelation(relation === rel ? "" : rel)}
                    className={cn(
                      "px-2.5 py-1 rounded-full text-xs font-medium border transition-colors",
                      relation === rel
                        ? "bg-foreground text-background border-foreground"
                        : "text-muted-foreground hover:text-foreground"
                    )}>
                    {rel}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Assets Section */}
          <div className="space-y-2">
            <label className="text-sm font-medium">
              Asset Access ({isCoOwner ? "All" : (isEditing ? selectedAssetIds.size : member.assetAccess.length)})
            </label>

            {isCoOwner ? (
              <div className="flex items-start gap-2 p-3 rounded-lg bg-blue-50 text-xs text-blue-700">
                <Info className="h-4 w-4 text-blue-500 mt-0.5 shrink-0" />
                Co-owners have full access to all current and future assets.
              </div>
            ) : !isEditing ? (
              member.assetAccess.length === 0 ? (
                <p className="text-xs text-muted-foreground">No asset access.</p>
              ) : (
                <div className="flex flex-wrap gap-1.5">
                  {member.assetAccess.map((a) => (
                    <span key={a.assetId} className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-secondary text-xs font-medium">
                      <Monitor className="h-3 w-3" /> {a.asset?.name ?? a.assetId}
                    </span>
                  ))}
                </div>
              )
            ) : assetsLoading ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground py-4">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading assets...
              </div>
            ) : assets.length === 0 ? (
              <p className="text-xs text-muted-foreground">No assets in this home yet.</p>
            ) : (
              <div className="space-y-1">
                <div className="flex items-center justify-between mb-1">
                  <button type="button" onClick={selectAllAssets}
                    className="text-xs text-blue-500 font-medium hover:underline">
                    {selectedAssetIds.size === assets.length ? "Deselect All" : "Select All"}
                  </button>
                  <span className="text-xs text-muted-foreground">{selectedAssetIds.size} of {assets.length}</span>
                </div>
                {assets.map((asset) => {
                  const isSelected = selectedAssetIds.has(asset.id);
                  return (
                    <button key={asset.id} type="button" onClick={() => toggleAsset(asset.id)}
                      className={cn("flex items-center gap-3 w-full p-2 rounded-lg border text-left transition-colors",
                        isSelected ? "border-foreground bg-secondary" : "hover:bg-secondary/50")}>
                      <Monitor className={cn("h-4 w-4 shrink-0", isSelected ? "text-foreground" : "text-muted-foreground")} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{asset.name}</p>
                        {asset.category && <p className="text-xs text-muted-foreground">{asset.category}</p>}
                      </div>
                      <div className={cn("w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0",
                        isSelected ? "border-foreground bg-foreground" : "border-muted-foreground")}>
                        {isSelected && <Check className="h-3 w-3 text-background" />}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Services Section */}
          <div className="space-y-2">
            <label className="text-sm font-medium">
              Service Access ({isCoOwner ? "All" : (isEditing ? selectedServices.size : member.serviceAccess.length)})
            </label>

            {isCoOwner ? (
              <div className="flex items-start gap-2 p-3 rounded-lg bg-blue-50 text-xs text-blue-700">
                <Info className="h-4 w-4 text-blue-500 mt-0.5 shrink-0" />
                Co-owners have full access to all services.
              </div>
            ) : !isEditing ? (
              member.serviceAccess.length === 0 ? (
                <p className="text-xs text-muted-foreground">No service access.</p>
              ) : (
                <div className="flex flex-wrap gap-1.5">
                  {member.serviceAccess.map((s) => (
                    <span key={s.serviceType} className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-secondary text-xs font-medium">
                      {SERVICE_ICONS[s.serviceType as ServiceType]} {SERVICE_TYPE_LABELS[s.serviceType as ServiceType]}
                    </span>
                  ))}
                </div>
              )
            ) : (
              <>
                <div className="flex items-center justify-between mb-1">
                  <button type="button" onClick={selectAllServices}
                    className="text-xs text-blue-500 font-medium hover:underline">
                    {selectedServices.size === ALL_SERVICE_TYPES.length ? "Deselect All" : "Select All"}
                  </button>
                  <span className="text-xs text-muted-foreground">{selectedServices.size} of {ALL_SERVICE_TYPES.length}</span>
                </div>
                <div className="space-y-1">
                  {ALL_SERVICE_TYPES.map((type) => {
                    const isSelected = selectedServices.has(type);
                    return (
                      <button key={type} type="button" onClick={() => toggleService(type)}
                        className={cn("flex items-center gap-3 w-full p-2 rounded-lg border text-left transition-colors",
                          isSelected ? "border-foreground bg-secondary" : "hover:bg-secondary/50")}>
                        <span className={isSelected ? "text-foreground" : "text-muted-foreground"}>{SERVICE_ICONS[type]}</span>
                        <span className="text-sm font-medium">{SERVICE_TYPE_LABELS[type]}</span>
                        <div className={cn("ml-auto w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0",
                          isSelected ? "border-foreground bg-foreground" : "border-muted-foreground")}>
                          {isSelected && <Check className="h-3 w-3 text-background" />}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </>
            )}
          </div>

          {/* Auto-grant future assets */}
          {!isCoOwner && (
            isEditing ? (
              <div className="flex items-center gap-3 p-3 rounded-lg bg-secondary/30">
                <div className="flex-1">
                  <p className="text-sm font-medium">Auto-grant future assets</p>
                  <p className="text-xs text-muted-foreground">Automatically grant access to new assets.</p>
                </div>
                <button type="button" onClick={() => setGrantFutureAssets((v) => !v)}
                  className={cn("relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors",
                    grantFutureAssets ? "bg-foreground" : "bg-muted-foreground/30")}>
                  <span className={cn("inline-block h-5 w-5 rounded-full bg-background shadow-sm transition-transform",
                    grantFutureAssets ? "translate-x-5" : "translate-x-0.5")} />
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Clock className="h-3.5 w-3.5" />
                Future assets: {member.grantFutureAssets ? "Auto-grant enabled" : "Manual only"}
              </div>
            )
          )}
        </div>

        <div className="shrink-0 border-t px-6 py-4 flex items-center justify-between gap-3">
          {isEditing ? (
            <>
              <Button variant="outline" onClick={toggleEdit}>Cancel</Button>
              <Button onClick={handleSave} disabled={saving}>
                {saving ? (<><Loader2 className="h-4 w-4 animate-spin mr-1" /> Saving...</>) : "Save Changes"}
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={handleRemove} disabled={removing}
                className="text-destructive hover:text-destructive">
                <Trash2 className="h-4 w-4 mr-1" /> Remove Member
              </Button>
              <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
